import type { AtMentionOption } from '../../types';
import createAtMentionBadge from '../createAtMentionBadge/createAtMentionBadge';

/**
 * ### convertMentionsToHTML
 *
 * @description
 * Converts {value} format to HTML badge elements for display.
 * Sanitizes input text with DOMPurify to prevent XSS attacks while preserving safe HTML formatting.
 *
 * @example
 * convertMentionsToHTML('Hello {johndoe} there')
 * // Returns: 'Hello <span class="mention-badge" ...>John Doe×</span> there'
 *
 * @param text - The text containing mentions in {value} format
 * @param options - Array of mention options to lookup labels
 * @returns HTML string with badge elements
 */
const convertMentionsToHTML = (text: string, options: AtMentionOption[] = []): string => {
  // Create a temporary div
  const tempDiv = document.createElement('div');

  // Regex to match {value} format
  const mentionRegex = /\{([^}]+)\}/g;

  let lastIndex = 0;
  let match;

  while ((match = mentionRegex.exec(text)) !== null) {
    // Add text before the mention (using innerHTML to preserve HTML tags like <b>, <i>)
    if (match.index > lastIndex) {
      const beforeText = text.substring(lastIndex, match.index);
      const beforeSpan = document.createElement('span');
      beforeSpan.innerHTML = beforeText;
      // Append all child nodes from the span to preserve the structure
      Array.from(beforeSpan.childNodes).forEach((node) => tempDiv.appendChild(node));
    }

    const value = match[1];
    if (!value) continue;

    // Find the option, fallback to value if not found
    const option = options.find((opt) => opt.value === value);
    const mentionOption = option;

    // Create badge element using the utility
    const badge = createAtMentionBadge(document.createElement('span'), mentionOption);
    tempDiv.appendChild(badge);

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after last mention (using innerHTML to preserve HTML tags)
  if (lastIndex < text.length) {
    const afterText = text.substring(lastIndex);
    const afterSpan = document.createElement('span');
    afterSpan.innerHTML = afterText;
    // Append all child nodes from the span to preserve the structure
    Array.from(afterSpan.childNodes).forEach((node) => tempDiv.appendChild(node));
  }

  return tempDiv.innerHTML;
};

export default convertMentionsToHTML;
